// NYC 2026 trip stops — see plan.md for the design.
//
// Coordinates are a first pass on the 658 x 1269 base-map grid (north up, west =
// low x, downtown = high y), placed on the green Manhattan body (never Staten Island).
// They are meant to be nudged once we see the map render. Notes are optional and
// filled in per stop.

import type {Stop} from "~/data/types"

export const stops: Record<string, Stop> = {
    // --- hotel + arrival/departure anchors ---
    "hotel": {
        id: "hotel",
        name: "Hotel",
        coord: {x: 116, y: 538},
        days: [1, 2, 3, 4, 5],
        notes: "Fairfield Inn & Suites with free breakfast and queen beds.",
    },
    "lincoln-tunnel": {
        id: "lincoln-tunnel",
        name: "Lincoln Tunnel",
        coord: {x: 14, y: 540},
        days: [1],
        notes: "Drove in from New Jersey through the tunnel and remembered the scene from Elf.",
        photos: ["lincoln-tunnel/1", "lincoln-tunnel/2"],
    },
    "lga": {
        id: "lga",
        name: "Airport",
        coord: {x: 560, y: 360},
        days: [5],
        notes: "Super smooth airport experience. We loved the water show!",
        photos: ["lga/1", "lga/2", "lga/3"],
    },

    // --- Day 1: Midtown & Times Square ---
    "macys": {
        id: "macys",
        name: "Macy's",
        coord: {x: 190, y: 548},
        days: [1],
        notes: "Checked out the toy section and celebrated the Knicks by shooting some hoops.",
        photos: ["macys/1", "macys/2", "macys/3"],
    },
    "times-square": {
        id: "times-square",
        name: "Times Square",
        coord: {x: 169, y: 528},
        days: [1],
        notes: "Dad got chosen as a guest in a street performance. Don't talk to the characters!",
        photos: ["times-square/1", "times-square/2", "times-square/3"],
    },
    "disney-store": {
        id: "disney-store",
        name: "Disney Store",
        coord: {x: 174, y: 516},
        days: [1],
        notes: "Unfortunately, they didn't have a Bitzee.",
        photos: ["disney-store/1", "disney-store/2", "disney-store/3"],
    },
    "mms-store": {
        id: "mms-store",
        name: "M&M's Store",
        coord: {x: 168, y: 504},
        days: [1],
        notes: "Picked up some chocolate for the kids as we walked around.",
        photos: ["mms-store/1", "mms-store/2", "mms-store/3"],
    },
    "rockefeller-center": {
        id: "rockefeller-center",
        name: "Rockefeller Center",
        coord: {x: 228, y: 510},
        days: [1],
        notes: "Stopped here to plan dinner and gaze at our Nintendo toys.",
        photos: [
            "rockefeller-center/1",
            "rockefeller-center/2",
            "rockefeller-center/3",
        ],
    },
    "nintendo-store": {
        id: "nintendo-store",
        name: "Nintendo Store",
        coord: {x: 238, y: 518},
        days: [1, 2],
        notes: "The highlight of the trip for the kids, who left with plenty of souveniers.",
        photos: ["nintendo-store/1", "nintendo-store/2", "nintendo-store/3"],
    },
    "st-patricks": {
        id: "st-patricks",
        name: "St. Patrick's Cathedral",
        coord: {x: 270, y: 510},
        days: [1],
        notes: "We lit a candle for Grandpa Dickie.",
        photos: ["st-patricks/1", "st-patricks/2", "st-patricks/3"],
    },
    "lego-store": {
        id: "lego-store",
        name: "LEGO Store",
        coord: {x: 250, y: 512},
        days: [1],
        notes: "Found some new LEGO sets to build, like the Luigi Mario Kart and the Slinky book end.",
        photos: ["lego-store/1", "lego-store/2", "lego-store/3"],
    },
    "johns-pizza": {
        id: "johns-pizza",
        name: "John's of Times Square",
        coord: {x: 127, y: 522},
        days: [1],
        notes: "Pizza in a converted church. Desperately needed after covering a lot of ground.",
        photos: ["johns-pizza/1", "johns-pizza/2", "johns-pizza/3"],
    },

    // --- Day 2: Downtown + Brooklyn ---
    "wtc": {
        id: "wtc",
        name: "World Trade Center",
        coord: {x: 165, y: 815},
        days: [2],
        notes: "Great views from the Cloudflare office on the 88th floor.",
        subwayLines: ["E"],
        photos: ["wtc/1", "wtc/2", "wtc/3"],
    },
    "oculus": {
        id: "oculus",
        name: "Oculus",
        coord: {x: 186, y: 824},
        days: [2, 4],
        notes: "Sick subway hub with great architecture. We stopped at Pop Mart twice for Labubus.",
        photos: ["oculus/1", "oculus/2", "oculus/3"],
    },
    "ground-zero": {
        id: "ground-zero",
        name: "Ground Zero",
        coord: {x: 168, y: 833},
        days: [2],
        notes: "Somber reminder amidst all the skyscrapers.",
        photos: ["ground-zero/1", "ground-zero/2", "ground-zero/3"],
    },
    "brooklyn-bridge": {
        id: "brooklyn-bridge",
        name: "Brooklyn Bridge",
        coord: {x: 388, y: 818},
        days: [2],
        notes: "Juju was super tired, so we took it slow. They don't allow locks anymore!",
        photos: ["brooklyn-bridge/1", "brooklyn-bridge/2", "brooklyn-bridge/3"],
    },
    "pebble-beach": {
        id: "pebble-beach",
        name: "Pebble Beach",
        coord: {x: 440, y: 786},
        days: [2],
        notes: "Naturally passed by here walking through the promenade.",
        photos: ["pebble-beach/1", "pebble-beach/2", "pebble-beach/3"],
    },
    "westville-dumbo": {
        id: "westville-dumbo",
        name: "Westville DUMBO",
        coord: {x: 482, y: 802},
        days: [2],
        notes: "Great pit stop to wait out the rain. Juju got a second wind and our drinks were super strong!",
        photos: ["westville-dumbo/1", "westville-dumbo/2", "westville-dumbo/3"],
    },
    "statue-liberty": {
        id: "statue-liberty",
        name: "Statue of Liberty",
        coord: {x: 100, y: 940},
        days: [2],
        notes: "We took a very chilly ferry ride around the island which passed by lady liberty.",
        photos: ["statue-liberty/1", "statue-liberty/2", "statue-liberty/3"],
    },
    "uss-intrepid": {
        id: "uss-intrepid",
        name: "USS Intrepid",
        coord: {x: 38, y: 526},
        days: [2],
        notes: "Walked right by this after exiting the ferry.",
        photos: ["uss-intrepid/1", "uss-intrepid/2"],
    },
    "grand-central": {
        id: "grand-central",
        name: "Grand Central Terminal",
        coord: {x: 300, y: 528},
        days: [2],
        notes: "We quickly walked through here before getting street dogs. One of the dirtier subway hubs, but still pretty.",
        photos: ["grand-central/1", "grand-central/2"],
    },
    "hot-dog-cart": {
        id: "hot-dog-cart",
        name: "Grand Central Hot Dog Cart",
        coord: {x: 296, y: 542},
        days: [2],
        notes: "Juju and Dad loved their hot dogs.",
        photos: ["hot-dog-cart/1"],
    },

    // --- Day 3: Central Park & Midtown & SoHo ---
    "liberty-bagels": {
        id: "liberty-bagels",
        name: "Liberty Bagels",
        coord: {x: 148, y: 544},
        days: [3],
        notes: "A classic NYC breakfast. Juju got the most colorful bagel you've ever seen.",
        photos: ["liberty-bagels/1", "liberty-bagels/2", "liberty-bagels/3"],
    },
    "central-park-zoo": {
        id: "central-park-zoo",
        name: "Central Park Zoo",
        coord: {x: 245, y: 470},
        days: [3],
        subwayLines: ["N"],
        notes: "Fairly small, but the sea lions in the center are super entertaining.",
        photos: [
            "central-park-zoo/1",
            "central-park-zoo/2",
            "central-park-zoo/3",
        ],
    },
    "central-park": {
        id: "central-park",
        name: "Central Park",
        coord: {x: 185, y: 425},
        days: [3],
        notes: "Strolled through the park and caught a bride and groom's first look.",
        photos: ["central-park/1", "central-park/2", "central-park/3"],
    },
    "el-mitote": {
        id: "el-mitote",
        name: "El Mitote",
        coord: {x: 100, y: 425},
        days: [3],
        notes: "Stopped here for some familiar Mexican food after a great walk through the upper west side.",
        photos: ["el-mitote/1", "el-mitote/2", "el-mitote/3"],
    },
    "apple-fifth": {
        id: "apple-fifth",
        name: "Apple Fifth Avenue",
        coord: {x: 262, y: 485},
        days: [3],
        subwayLines: ["1"],
        notes: "Great stop for us to rest in the air conditioning and for the kids to play some games.",
        photos: ["apple-fifth/1", "apple-fifth/2", "apple-fifth/3"],
    },
    "368-broadway": {
        id: "368-broadway",
        name: "368 Broadway",
        coord: {x: 274, y: 800},
        days: [3],
        notes: "We caught a quick glimpse of Casey Neistat riding away on his boosted board!",
        links: [
            {
                label: "The Biggest Parade in New York City History",
                url: "https://youtu.be/hqxUqRfceEQ",
            },
            {
                label: "This Robot Changed How I Vlog",
                url: "https://youtu.be/YxJIHbHXVrs",
            },
            {
                label: "Jennifer Lopez said I Am Not a New Yorker",
                url: "https://youtu.be/XJBm88g-e8Q",
            },
        ],
        subwayLines: ["E"],
        photos: ["368-broadway/1", "368-broadway/2", "368-broadway/3"],
    },
    "washington-square": {
        id: "washington-square",
        name: "Washington Square Park",
        coord: {x: 260, y: 686},
        days: [3],
        subwayLines: ["N"],
        notes: "Little too much weed for the kids.",
        photos: ["washington-square/1"],
    },
    "the-cage": {
        id: "the-cage",
        name: "The Cage",
        coord: {x: 248, y: 692},
        days: [3],
        notes: "Some super intense basketball was being played here!",
    },
    "burgerology": {
        id: "burgerology",
        name: "Burgerology",
        coord: {x: 128, y: 538},
        days: [3],
        notes: "We caught a world cup game while we ate.",
        subwayLines: ["E"],
    },

    // --- Day 4: West Side & Chinatown ---
    "bh-photo": {
        id: "bh-photo",
        name: "B&H Photo",
        coord: {x: 98, y: 552},
        days: [4],
        notes: "More gear than I've ever seen. We got to see the conveyor belts working!",
        photos: ["bh-photo/1"],
    },
    "vessel": {
        id: "vessel",
        name: "Vessel",
        coord: {x: 74, y: 560},
        days: [4],
        notes: "Outside of a high end mall alongside some World Cup festivities.",
        photos: ["vessel/1", "vessel/2", "vessel/3"],
    },
    "hudson-yards": {
        id: "hudson-yards",
        name: "Hudson Yards",
        coord: {x: 60, y: 550},
        days: [4],
        notes: "Showed the kids the train yard.",
        photos: ["hudson-yards/1", "hudson-yards/2", "hudson-yards/3"],
    },
    "high-line": {
        id: "high-line",
        name: "High Line",
        coord: {x: 62, y: 618},
        days: [4],
        notes: "Super fun walk with great smells of the flowers and views of the buildings.",
        photos: ["high-line/1", "high-line/2", "high-line/3"],
    },
    "chelsea-market": {
        id: "chelsea-market",
        name: "Chelsea Market",
        coord: {x: 72, y: 662},
        days: [4],
        notes: "Got lunch and chocolates after walking the High Line.",
        photos: ["chelsea-market/1", "chelsea-market/2", "chelsea-market/3"],
    },
    "little-island": {
        id: "little-island",
        name: "Little Island",
        coord: {x: 42, y: 674},
        days: [4],
        notes: "Covered every inch of this small park.",
        photos: ["little-island/1", "little-island/2", "little-island/3"],
    },
    "chinatown": {
        id: "chinatown",
        name: "Chinatown",
        coord: {x: 338, y: 794},
        days: [4],
        subwayLines: ["A", "B"],
        notes: "Saw cooked ducks and met some nice people who let us try their bao.",
        photos: ["chinatown/1", "chinatown/2", "chinatown/3"],
    },
    "nyse": {
        id: "nyse",
        name: "New York Stock Exchange",
        coord: {x: 260, y: 892},
        days: [4],
        subwayLines: ["J"],
        notes: "Quickly walked through the financial district.",
        photos: ["nyse/1"],
    },
    "wall-street": {
        id: "wall-street",
        name: "Wall Street Bull",
        coord: {x: 252, y: 906},
        days: [4],
        notes: "This was surprisingly busy.",
        photos: ["wall-street/1", "wall-street/2"],
    },
    "pizza-suprema": {
        id: "pizza-suprema",
        name: "NY Pizza Suprema",
        coord: {x: 126, y: 570},
        days: [4],
        notes: "Packed pizza place that was really good!",
        subwayLines: ["E"],
        photos: ["pizza-suprema/1", "pizza-suprema/2", "pizza-suprema/3"],
    },
    "madison-square-garden": {
        id: "madison-square-garden",
        name: "Madison Square Garden",
        coord: {x: 128, y: 556},
        days: [4],
        notes: "Buzzing all week due to the Knicks championship.",
        photos: [
            "madison-square-garden/1",
            "madison-square-garden/2",
            "madison-square-garden/3",
        ],
    },
    "penn-station": {
        id: "penn-station",
        name: "Penn Station",
        coord: {x: 142, y: 556},
        days: [4],
        notes: "Another major subway hub where we got ice cream and walked around Madison Square Garden.",
        photos: ["penn-station/1", "penn-station/2", "penn-station/3"],
    },
}
